import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  collection,
  getDocs,
  runTransaction,
  serverTimestamp,
  addDoc, query, orderBy, limit
} from 'firebase/firestore';
import { db } from '../firebase';
import { formatDistanceToNow } from 'date-fns';
import { starCoinsClient } from '../services/starCoinsClient';

const DAILY_MESSAGE_LIMIT = 3; 
const TIER_1_LIMIT = 30;  // messages per minute
const TIER_2_LIMIT = 45;  // messages per minute
const ABUSE_LIMIT = 60;   // messages per minute trigger for abuse
const TIER_2_COOLDOWN = 5000;  // 5 seconds in milliseconds
const ABUSE_LOCKOUT = 24 * 60 * 60 * 1000;  // 24 hours in milliseconds
const TIER_RESET_TIME = 5 * 60 * 1000;  // 5 minutes in milliseconds

// PickStar limits - now matching GamePredictionModal exactly
const PICKSTAR_PRO_LIMIT = 7;    // 7 uses per reset period for pro users
const PICKSTAR_BASIC_LIMIT = 1;  // 1 use per reset period for basic users
const PICKSTAR_PRO_RESET_HOURS = 3;    // 2 hour reset for pro
const PICKSTAR_BASIC_RESET_HOURS = 12; // 24 hour reset for basic

// Custom Prompts limits - Update these
const PROMPT_BASIC_COUNT_LIMIT = 1;    // Keep same (max number of prompts)
const PROMPT_PRO_COUNT_LIMIT = 3;     // Keep same (max number of prompts)
const PROMPT_BASIC_USAGE_LIMIT = 1;    // Change from 5 to 1 (uses per day)
const PROMPT_PRO_USAGE_LIMIT = 7;      // Change from 50 to 7 (uses per day)

// xPicks limits configuration
const XPICKS_DAILY_LIMIT = 2;     // 2 free uses per day for all users
const XPICKS_RESET_HOURS = 24;    // Daily reset

// Star Coins configuration
const STAR_COINS_CONFIG = {
  PACKAGES: [
    { id: 'basic', amount: 100, price: 4.99, proPrice: 3.99, popular: false }, //place holder
    { id: 'value', amount: 250, price: 9.99, proPrice: 7.99, popular: true },
    { id: 'premium', amount: 600, price: 19.99, proPrice: 15.99, popular: false },
    { id: 'ultimate', amount: 1500, price: 39.99, proPrice: 29.99, popular: false }
  ],
  COSTS: {
    customPromptCreation: 33,  // Updated from 10 to 33
    customPromptUse: 11,       // Updated from 5 to 11
    builtInPromptUse: 5,       // Keep as-is
    aiMessage: 11,             // Updated from 5 to 11
    aiSlotSpin: 7,             // Cost for predictions and slot spins
    xPickSpin: 9,              // Cost for xPicks after free uses
    premiumPrompt: 15          // Keep as-is
  }
};

// Only log in development
const logDebug = (message, data) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(message, data);
  }
};

// Error logging for development
const logError = (message, error) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(message, error);
  }
};

export class UsageLimiter {
  constructor() {
    this.db = db;
    this.config = {
      tierLimits: {
        basic: {
          dailyMessageLimit: DAILY_MESSAGE_LIMIT,
          pickstarLimit: PICKSTAR_BASIC_LIMIT,
          pickstarResetHours: PICKSTAR_BASIC_RESET_HOURS,
          promptCountLimit: PROMPT_BASIC_COUNT_LIMIT,
          promptUsageLimit: PROMPT_BASIC_USAGE_LIMIT,
          promptResetHours: 24
        },
        pro: {
          dailyMessageLimit: 40,
          tier1Limit: TIER_1_LIMIT,
          tier2Limit: TIER_2_LIMIT,
          abuseLimit: ABUSE_LIMIT,
          tier2Cooldown: TIER_2_COOLDOWN,
          abuseLockout: ABUSE_LOCKOUT,
          tierResetTime: TIER_RESET_TIME,
          pickstarLimit: PICKSTAR_PRO_LIMIT,
          pickstarResetHours: PICKSTAR_PRO_RESET_HOURS,
          promptCountLimit: PROMPT_PRO_COUNT_LIMIT,
          promptUsageLimit: PROMPT_PRO_USAGE_LIMIT,
          promptResetHours: 24
        }
      },
      adminEmails: [
        'ronaldsholt@gmail.com',
        'lucaas.sn@gmail.com'    
      ]
    };
  }
  async checkUserLimit(userId) {
    if (!userId) {
      console.log("UsageLimiter - No userId provided");
      return { canSendMessage: false };
    }
  
    const userDoc = await getDoc(doc(this.db, 'users', userId));
    if (!userDoc.exists()) {
      return { canSendMessage: false };
    }
  
    const userData = userDoc.data();
  
    // Admin bypass
    if (this.config.adminEmails.includes(userData.email)) {
      return { 
        canSendMessage: true,
        tier: userData.tier || 'basic',
      };
    }
  
    // Pro users have BOTH daily limits AND rate limiting
    if (userData.tier === 'pro') {
      // Initialize usage data if it doesn't exist
      const usage = userData.usage || {
        messageCount: 0,
        lastReset: null,
        lastMessageTime: null
      };
  
      // Only set initial usage data if it's completely missing
      if (!userData.usage) {
        await setDoc(doc(this.db, 'users', userId), {
          usage: {
            ...usage,
            lastReset: serverTimestamp()
          }
        }, { merge: true });
      }
  
      const today = this.getCurrentDate();
      const lastReset = usage.lastReset?.toDate() || new Date(0);
      const lastResetDate = lastReset.toISOString().split('T')[0];
  
      // If it's a new day, reset the count
      if (lastResetDate !== today) {
        await this.resetUserUsage(userId);
        return { 
          canSendMessage: true,
          messageCount: 0,
          remainingMessages: this.config.tierLimits.pro.dailyMessageLimit,
          nextResetTime: this.getNextResetTime(),
          tier: 'pro'
        };
      }
  
      const messageCount = usage.messageCount || 0;
  
      // Check daily limit first (36 messages for pro)
      if (messageCount >= this.config.tierLimits.pro.dailyMessageLimit) {
        const starCoinsBalance = userData.starCoins?.balance || 0;
        const starCoinsCost = STAR_COINS_CONFIG.COSTS.aiMessage;
        const canUseStarCoins = starCoinsBalance >= starCoinsCost;
        
        return {
          canSendMessage: canUseStarCoins,
          messageCount,
          remainingMessages: 0,
          nextResetTime: this.getNextResetTime(),
          tier: 'pro',
          useStarCoins: canUseStarCoins,
          starCoinsBalance,
          starCoinsCost
        };
      }
  
      // Then check rate limiting (messages per minute)
      const rateLimitStatus = await this.checkRateLimit(userId);
      if (!rateLimitStatus.canSendMessage) {
        return {
          ...rateLimitStatus,
          messageCount,
          remainingMessages: this.config.tierLimits.pro.dailyMessageLimit - messageCount,
          nextResetTime: this.getNextResetTime(),
          tier: 'pro'
        };
      }
  
      // Both daily and rate limits are good
      return {
        canSendMessage: true,
        messageCount,
        remainingMessages: this.config.tierLimits.pro.dailyMessageLimit - messageCount,
        nextResetTime: this.getNextResetTime(),
        tier: 'pro'
      };
    }
  
    // Basic users logic (unchanged)
    // Initialize usage data if it doesn't exist
    const usage = userData.usage || {
      messageCount: 0,
      lastReset: null,
      lastMessageTime: null
    };
  
    // Only set initial usage data if it's completely missing
    if (!userData.usage) {
      await setDoc(doc(this.db, 'users', userId), {
        usage: {
          ...usage,
          lastReset: serverTimestamp()
        }
      }, { merge: true });
    }
  
    const today = this.getCurrentDate();
    const lastReset = usage.lastReset?.toDate() || new Date(0);
    const lastResetDate = lastReset.toISOString().split('T')[0];
  
    // If it's a new day, reset the count
    if (lastResetDate !== today) {
      await this.resetUserUsage(userId);
      return { 
        canSendMessage: true,
        messageCount: 0,
        remainingMessages: this.config.tierLimits.basic.dailyMessageLimit,
        nextResetTime: this.getNextResetTime(),
        tier: userData.tier || 'basic'
      };
    }
  
    const messageCount = usage.messageCount || 0;
  
    // Check if user has hit their daily limit
    if (messageCount >= this.config.tierLimits.basic.dailyMessageLimit) {
      const starCoinsBalance = userData.starCoins?.balance || 0;
      const starCoinsCost = STAR_COINS_CONFIG.COSTS.aiMessage;
      const canUseStarCoins = starCoinsBalance >= starCoinsCost;
      return {
        canSendMessage: canUseStarCoins,
        messageCount,
        remainingMessages: 0,
        nextResetTime: this.getNextResetTime(),
        tier: userData.tier || 'basic',
        useStarCoins: canUseStarCoins,
        starCoinsBalance,
        starCoinsCost
      };
    }
  
    return {
      canSendMessage: true,
      messageCount,
      remainingMessages: this.config.tierLimits.basic.dailyMessageLimit - messageCount,
      nextResetTime: this.getNextResetTime(),
      tier: userData.tier || 'basic'
    };
  }
  // async checkUserLimit(userId) {
  //   if (!userId) {
  //     console.log("UsageLimiter - No userId provided");
  //     return { canSendMessage: false };
  //   }

  //   const userDoc = await getDoc(doc(this.db, 'users', userId));
  //   if (!userDoc.exists()) {
  //     return { canSendMessage: false };
  //   }

  //   const userData = userDoc.data();

  //   // Admin bypass
  //   if (this.config.adminEmails.includes(userData.email)) {
  //     return { 
  //       canSendMessage: true,
  //       tier: userData.tier || 'basic',
  //     };
  //   }

  //   // Pro users have rate limiting
  //   if (userData.tier === 'pro') {
  //     const rateLimitStatus = await this.checkRateLimit(userId);
  //     return {
  //       ...rateLimitStatus,
  //       tier: 'pro'
  //     };
  //   }

  //   // Initialize usage data if it doesn't exist
  //   const usage = userData.usage || {
  //     messageCount: 0,
  //     lastReset: null,
  //     lastMessageTime: null
  //   };

  //   // Only set initial usage data if it's completely missing
  //   if (!userData.usage) {
  //     await setDoc(doc(this.db, 'users', userId), {
  //       usage: {
  //         ...usage,
  //         lastReset: serverTimestamp()
  //       }
  //     }, { merge: true });
  //   }

  //   const today = this.getCurrentDate();
  //   const lastReset = usage.lastReset?.toDate() || new Date(0);
  //   const lastResetDate = lastReset.toISOString().split('T')[0];

  //   // If it's a new day, reset the count
  //   if (lastResetDate !== today) {
  //     await this.resetUserUsage(userId);
  //     return { 
  //       canSendMessage: true,
  //       messageCount: 0,
  //       remainingMessages: this.config.tierLimits.basic.dailyMessageLimit,
  //       nextResetTime: this.getNextResetTime(),
  //       tier: userData.tier || 'basic'
  //     };
  //   }

  //   const messageCount = usage.messageCount || 0;

  //   // Check if user has hit their daily limit
  //   if (messageCount >= this.config.tierLimits.basic.dailyMessageLimit) {
  //     const starCoinsBalance = userData.starCoins?.balance || 0;
  //     const starCoinsCost = STAR_COINS_CONFIG.COSTS.aiMessage;
  //     const canUseStarCoins = starCoinsBalance >= starCoinsCost;
  //     return {
  //       // canSendMessage: false,
  //       canSendMessage: canUseStarCoins,
  //       messageCount,
  //       remainingMessages: 0,
  //       nextResetTime: this.getNextResetTime(),
  //       tier: userData.tier || 'basic',
  //       useStarCoins: canUseStarCoins,  // add for coin messaging
  //       starCoinsBalance,
  //       starCoinsCost
  //     };
  //   }

  //   return {
  //     canSendMessage: true,
  //     messageCount,
  //     remainingMessages: this.config.tierLimits.basic.dailyMessageLimit - messageCount,
  //     nextResetTime: this.getNextResetTime(),
  //     tier: userData.tier || 'basic'
  //   };
  // }

  async incrementUsage(userId) {
    if (!userId) return;

    // NEW: Check if we should use Star Coins instead
    const limitStatus = await this.checkUserLimit(userId);

    console.log('[UsageLimiter] incrementUsage called:', {
      messageCount: limitStatus.messageCount,
      remainingMessages: limitStatus.remainingMessages,
      useStarCoins: limitStatus.useStarCoins,
      starCoinsCost: limitStatus.starCoinsCost
    });

    if (limitStatus.useStarCoins) {
      console.log('[UsageLimiter] Using Star Coins for message - deducting', limitStatus.starCoinsCost, 'coins');
      // Use the same Star Coins service as GamePredictionModal
      const result = await starCoinsClient.consumeStarCoins(limitStatus.starCoinsCost, 'aiMessage');

      if (!result.success) {
        throw new Error(result.error || 'Failed to consume Star Coins');
      }

      console.log('[UsageLimiter] Successfully consumed Star Coins');
      return; // Don't increment regular usage counter
    }

    console.log('[UsageLimiter] Using free message (', limitStatus.remainingMessages, 'remaining )');

    const timestamp = Date.now();
    const userRef = doc(this.db, 'users', userId);

    try {
      // First do the transaction and capture returned userData
      const userData = await runTransaction(this.db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) return;

        const userData = userDoc.data();
        const currentCount = userData.usage?.messageCount || 0;

        transaction.update(userRef, {
          'usage.messageCount': currentCount + 1,
          'usage.lastMessageTime': serverTimestamp()
        });

        return userData;
      });

      // Then update rate limit separately if we got userData and it's a pro user
      if (userData?.tier === 'pro') {
        await this.updateRateLimit(userId, timestamp);
      }
    } catch (error) {
      logError('Error in incrementUsage:', error);
      throw error;
    }
  }

  getCurrentDate() {
    return new Date().toISOString().split('T')[0];
  }

  getNextResetTime() {
    const resetTime = new Date();
    resetTime.setHours(resetTime.getHours() + 24); // Set to 24 hours from now
    return resetTime;
  }

  async resetUserUsage(userId) {
    const userRef = doc(this.db, 'users', userId);
    await setDoc(userRef, {
      usage: {
        messageCount: 0,
        lastReset: serverTimestamp(),
        lastMessageTime: null,
        rateLimit: {
          tier: 1,
          minuteCount: 0,
          lastTierReset: serverTimestamp(),
          lastMessageTimestamps: [],
          abuseLockoutUntil: null
        }
      }
    }, { merge: true });
  }

  async checkRateLimit(userId) {
    if (!userId) return { canSendMessage: false };

    const userRef = doc(this.db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) return { canSendMessage: false };

    const userData = userDoc.data();
    if (userData.tier !== 'pro') return { canSendMessage: true };

    const rateLimit = userData.usage?.rateLimit || {
      tier: 1,
      minuteCount: 0,
      lastTierReset: null,
      lastMessageTimestamps: [],
      abuseLockoutUntil: null
    };

    // Unified timestamp filtering logic
    const now = new Date();
    const oneMinuteAgo = new Date(now - 60000);
    const recentMessages = (rateLimit.lastMessageTimestamps || [])
      .filter(timestamp => {
        if (timestamp && typeof timestamp.toDate === 'function') {
          return timestamp.toDate() > oneMinuteAgo;
        }
        return false;
      });

    // Check if user is in abuse lockout
    if (rateLimit.abuseLockoutUntil) {
      const lockoutUntil = rateLimit.abuseLockoutUntil.toDate();
      if (lockoutUntil > now) {
        return {
          canSendMessage: false,
          cooldownMs: lockoutUntil - now,
          reason: 'abuse_lockout'
        };
      } else {
        await this.resetRateLimit(userId);
        return { canSendMessage: true };
      }
    }

    // Check for abuse threshold
    if (recentMessages.length >= ABUSE_LIMIT) {
      await this.flagAbuse(userId);
      return {
        canSendMessage: false,
        cooldownMs: ABUSE_LOCKOUT,
        reason: 'abuse_detected'
      };
    }

    // Check tier-based cooldowns
    if (recentMessages.length >= TIER_2_LIMIT) {
      const lastMessage = rateLimit.lastMessageTimestamps[rateLimit.lastMessageTimestamps.length - 1];
      if (lastMessage && typeof lastMessage.toDate === 'function') {
        const timeSinceLastMessage = now - lastMessage.toDate();
        if (timeSinceLastMessage < TIER_2_COOLDOWN) {
          return {
            canSendMessage: false,
            cooldownMs: TIER_2_COOLDOWN - timeSinceLastMessage,
            reason: 'tier2_cooldown'
          };
        }
      }
    }

    return { canSendMessage: true };
  }

  async updateRateLimit(userId, messageTimestamp) {
    const userRef = doc(this.db, 'users', userId);
    await runTransaction(this.db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) return;

      const userData = userDoc.data();
      if (userData.tier !== 'pro') return;

      // Keep separate counters for rate limiting and UI
      const rateLimit = userData.usage?.rateLimit || {};
      const now = new Date();
      const oneMinuteAgo = new Date(now - 60000);
      const recentMessages = (rateLimit.lastMessageTimestamps || [])
        .filter(timestamp => timestamp.toDate() > oneMinuteAgo);

      // Use a regular Date object instead of serverTimestamp()
      recentMessages.push(now);

      transaction.update(userRef, {
        'usage.rateLimit.lastMessageTimestamps': recentMessages,
        'usage.rateLimit.minuteCount': recentMessages.length,
        'usage.rateLimit.lastTierReset': serverTimestamp() // This is fine outside the array
      });
    });
  }

  async flagAbuse(userId) {
    const userRef = doc(this.db, 'users', userId);
    await setDoc(userRef, {
      'usage.rateLimit.abuseLockoutUntil': serverTimestamp(),
      'usage.rateLimit.tier': 3
    }, { merge: true });
  }

  async resetRateLimit(userId) {
    const userRef = doc(this.db, 'users', userId);
    await setDoc(userRef, {
      'usage.rateLimit': {
        tier: 1,
        minuteCount: 0,
        lastTierReset: serverTimestamp(),
        lastMessageTimestamps: [],
        abuseLockoutUntil: null
      }
    }, { merge: true });
  }

  // Enhanced checkSlotMachineLimit with consistent naming/format
  // Enhanced checkSlotMachineLimit with Star Coins integration - Fix for infinite update loop
async checkSlotMachineLimit(userId) {
  if (!userId) {
    return { canUse: false, remaining: 0, resetTime: null };
  }

  try {
    const userRef = doc(this.db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { canUse: false, remaining: 0, resetTime: null };
    }
  
    const userData = userDoc.data();
    
    // Admin bypass
    if (this.config.adminEmails.includes(userData.email)) {
      return { canUse: true, remaining: 999, resetTime: null, tier: userData.tier || 'pro', useStarCoins: false };
    }
  
    // Get user tier and set limits accordingly
    const tier = userData.tier || 'basic';
    
    const slotUsage = userData.slotMachineUsage || {
      count: 0,
      lastReset: null
    };
  
    // Get limits based on user tier
    const tierConfig = this.config.tierLimits[tier] || this.config.tierLimits.basic;
    const limit = tierConfig.pickstarLimit;
    const resetHours = tierConfig.pickstarResetHours;
    
    const now = new Date();
    const lastReset = slotUsage.lastReset?.toDate() || new Date(0);
    
    // Calculate hours since last reset
    const hoursSinceReset = (now - lastReset) / (1000 * 60 * 60);
    
    // Reset if enough time has passed
    if (hoursSinceReset >= resetHours) {
      try {
        await setDoc(userRef, {
          slotMachineUsage: {
            count: 0,
            lastReset: serverTimestamp()
          }
        }, { merge: true });
      } catch (resetError) {
        console.error('Error resetting slot machine usage:', resetError);
        // Continue with current status even if reset fails
      }
      
      const resetTime = this.calculateNextResetTime(now, resetHours);
      
      return { 
        canUse: true, 
        remaining: limit,
        resetTime,
        tier,
        useStarCoins: false
      };
    }
  
    const usageCount = slotUsage.count || 0;
    const freeSpinsRemaining = Math.max(0, limit - usageCount);
    const canUseFreeSpins = usageCount < limit;
    
    // Check Star Coins balance - this is important for the new logic
    const starCoinsBalance = userData.starCoins?.balance || 0;
    const STAR_COINS_COST = STAR_COINS_CONFIG.COSTS.aiSlotSpin; // Get cost from config
    const canUseStarCoins = starCoinsBalance >= STAR_COINS_COST;
    
    // Calculate time until next reset
    const resetTime = this.calculateNextResetTime(lastReset, resetHours);
    
    // Important: Return a stable result structure to avoid unnecessary re-renders
    return { 
      canUse: canUseFreeSpins || canUseStarCoins, 
      remaining: freeSpinsRemaining,
      resetTime,
      tier,
      useStarCoins: !canUseFreeSpins && canUseStarCoins,
      starCoinsBalance,
      starCoinsCost: STAR_COINS_COST
    };
  } catch (error) {
    console.error('[UsageLimiter] Error checking slot machine limit:', error);
    return { 
      canUse: false, 
      remaining: 0, 
      resetTime: null, 
      tier: 'basic', 
      error: true,
      useStarCoins: false
    };
  }
}

  // Calculate the next reset time
  calculateNextResetTime(baseTime, hoursToAdd) {
    const resetTime = new Date(baseTime);
    resetTime.setHours(resetTime.getHours() + hoursToAdd);
    return resetTime;
  }

  // Get local date string in YYYY-MM-DD format
  // CRITICAL UPDATE (2025-09-29): Added for consistent local timezone handling
  // Ensures all users see reset at their local midnight, not UTC midnight
  getLocalDateString(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Get next midnight in local timezone
  // CRITICAL UPDATE (2025-09-29): Added for countdown timer display
  // Returns exact timestamp of next midnight for "Resets in Xh Ym" display
  getNextMidnight() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  }
  
  // Enhanced incrementSlotMachineUsage with optimized error handling
  async incrementSlotMachineUsage(userId) {
    if (!userId) {
      return { success: false, error: 'No userId provided' };
    }
  
    try {
      const userRef = doc(this.db, 'users', userId);
      
      // First check if the user can use this and how
      const status = await this.checkSlotMachineLimit(userId);
      
      // If they can't use it at all (no free spins AND not enough Star Coins)
      if (!status.canUse) {
        return { 
          success: false, 
          error: 'Usage limit reached and insufficient Star Coins',
          remaining: status.remaining,
          resetTime: status.resetTime,
          useStarCoins: false
        };
      }
      
      // If they should use Star Coins instead of free spins
      if (status.useStarCoins) {
        // Consume Star Coins
        try {
          // Deduct Star Coins using a transaction for safety
          await runTransaction(this.db, async (transaction) => {
            const userDoc = await transaction.get(userRef);
            if (!userDoc.exists()) return;
            
            const userData = userDoc.data();
            const currentBalance = userData.starCoins?.balance || 0;
            
            if (currentBalance < status.starCoinsCost) {
              throw new Error('Insufficient Star Coins balance');
            }
            
            // Update the Star Coins balance
            transaction.update(userRef, {
              'starCoins.balance': currentBalance - status.starCoinsCost
            });
            
            // Also log the transaction
            const transactionRef = collection(this.db, 'users', userId, 'starCoinsTransactions');
            transaction.set(doc(transactionRef), {
              type: 'spend',
              amount: status.starCoinsCost,
              feature: 'gamePrediction',
              timestamp: serverTimestamp(),
              newBalance: currentBalance - status.starCoinsCost,
              description: 'Game Prediction using Star Coins'
            });
          });
          
          return { 
            success: true, 
            remaining: status.remaining,
            resetTime: status.resetTime,
            useStarCoins: true,
            starCoinsUsed: status.starCoinsCost
          };
        } catch (error) {
          logError('[UsageLimiter] Error consuming Star Coins:', error);
          return { 
            success: false, 
            error: error.message || 'Error consuming Star Coins',
            remaining: status.remaining,
            resetTime: status.resetTime,
            useStarCoins: true
          };
        }
      }
      
      // Use free spins (standard behavior)
      // Use transaction to safely update the count
      await runTransaction(this.db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) return;
    
        const currentUsage = userDoc.data().slotMachineUsage || {
          count: 0,
          lastReset: null
        };
    
        transaction.update(userRef, {
          'slotMachineUsage.count': currentUsage.count + 1,
          'slotMachineUsage.lastReset': currentUsage.lastReset || serverTimestamp()
        });
      });
      
      // Get updated status after increment
      const updatedStatus = await this.checkSlotMachineLimit(userId);
      
      return { 
        success: true, 
        remaining: updatedStatus.remaining,
        resetTime: updatedStatus.resetTime,
        useStarCoins: false
      };
    } catch (error) {
      logError('[UsageLimiter] Error incrementing usage:', error);
      return { 
        success: false, 
        error: error.message,
        incrementApplied: false,
        useStarCoins: false
      };
    }
  }

  // Check xPicks usage limit (2 free per day, then 7 coins)
  async checkXPicksLimit(userId) {
    if (!userId) {
      return { canUse: false, remaining: 0, resetTime: null };
    }

    try {
      const userRef = doc(this.db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return { canUse: false, remaining: 0, resetTime: null };
      }

      const userData = userDoc.data();

      // Admin bypass
      if (this.config.adminEmails.includes(userData.email)) {
        return { canUse: true, remaining: 999, resetTime: null, useStarCoins: false };
      }

      // Get xPicks usage for today
      const xPicksUsage = userData.xPicksUsage || {
        count: 0,
        lastReset: null
      };

      const now = new Date();
      const lastReset = xPicksUsage.lastReset?.toDate() || new Date(0);

      // Calculate hours since last reset
      const hoursSinceReset = (now - lastReset) / (1000 * 60 * 60);

      // Reset if 24 hours have passed
      if (hoursSinceReset >= XPICKS_RESET_HOURS) {
        await setDoc(userRef, {
          xPicksUsage: {
            count: 0,
            lastReset: serverTimestamp()
          }
        }, { merge: true });

        const resetTime = this.calculateNextResetTime(now, XPICKS_RESET_HOURS);

        return {
          canUse: true,
          remaining: XPICKS_DAILY_LIMIT,
          resetTime,
          useStarCoins: false
        };
      }

      const usageCount = xPicksUsage.count || 0;
      const freeUsesRemaining = Math.max(0, XPICKS_DAILY_LIMIT - usageCount);
      const canUseFree = usageCount < XPICKS_DAILY_LIMIT;

      // Check Star Coins balance if free uses are exhausted
      const starCoinsBalance = userData.starCoins?.balance || 0;
      const STAR_COINS_COST = STAR_COINS_CONFIG.COSTS.xPickSpin;
      const canUseStarCoins = starCoinsBalance >= STAR_COINS_COST;

      // Calculate time until next reset
      const resetTime = this.calculateNextResetTime(lastReset, XPICKS_RESET_HOURS);

      return {
        canUse: canUseFree || canUseStarCoins,
        remaining: freeUsesRemaining,
        resetTime,
        useStarCoins: !canUseFree && canUseStarCoins,
        starCoinsBalance,
        starCoinsCost: STAR_COINS_COST
      };
    } catch (error) {
      console.error('[UsageLimiter] Error checking xPicks limit:', error);
      return {
        canUse: false,
        remaining: 0,
        resetTime: null,
        error: true,
        useStarCoins: false
      };
    }
  }

  // Increment xPicks usage (handles both free and coin usage)
  async incrementXPicksUsage(userId) {
    if (!userId) {
      return { success: false, error: 'No userId provided' };
    }

    try {
      const userRef = doc(this.db, 'users', userId);

      // First check if the user can use xPicks
      const status = await this.checkXPicksLimit(userId);

      if (!status.canUse) {
        return {
          success: false,
          error: 'Usage limit reached and insufficient Star Coins',
          remaining: status.remaining,
          resetTime: status.resetTime,
          useStarCoins: false
        };
      }

      // If they should use Star Coins
      if (status.useStarCoins) {
        console.log('[UsageLimiter] Using Star Coins for xPicks:', status.starCoinsCost);
        try {
          // Use starCoinsClient to handle the consumption properly through Cloud Functions
          const result = await starCoinsClient.consumeStarCoins(status.starCoinsCost, 'xPicks');
          console.log('[UsageLimiter] Star Coins consumption result:', result);

          if (!result.success) {
            console.error('[UsageLimiter] Failed to consume Star Coins:', result.error);
            return {
              success: false,
              error: result.error || 'Failed to consume Star Coins',
              remaining: status.remaining,
              resetTime: status.resetTime,
              useStarCoins: true
            };
          }

          console.log('[UsageLimiter] Successfully consumed Star Coins, new balance:', result.newBalance);
        } catch (error) {
          console.error('[UsageLimiter] Error consuming Star Coins for xPicks:', error);
          return {
            success: false,
            error: error.message || 'Error consuming Star Coins',
            remaining: status.remaining,
            resetTime: status.resetTime,
            useStarCoins: true
          };
        }

        return {
          success: true,
          remaining: status.remaining,
          resetTime: status.resetTime,
          useStarCoins: true,
          starCoinsUsed: status.starCoinsCost
        };
      }

      // Use free xPicks
      await runTransaction(this.db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) return;

        const currentUsage = userDoc.data().xPicksUsage || {
          count: 0,
          lastReset: null
        };

        transaction.update(userRef, {
          'xPicksUsage.count': currentUsage.count + 1,
          'xPicksUsage.lastReset': currentUsage.lastReset || serverTimestamp()
        });
      });

      // Get updated status after increment
      const updatedStatus = await this.checkXPicksLimit(userId);

      return {
        success: true,
        remaining: updatedStatus.remaining,
        resetTime: updatedStatus.resetTime,
        useStarCoins: false
      };
    } catch (error) {
      logError('[UsageLimiter] Error incrementing xPicks usage:', error);
      return {
        success: false,
        error: error.message,
        useStarCoins: false
      };
    }
  }

  // Check if user can create more prompts
  async checkPromptCreateLimit(userId) {
    if (!userId) {
      return { canCreate: false, remaining: 0, maxPrompts: 0 };
    }

    try {
      const userRef = doc(this.db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return { canCreate: false, remaining: 0, maxPrompts: 0 };
      }
      
      const userData = userDoc.data();
      
      // Admin bypass
      if (this.config.adminEmails.includes(userData.email)) {
        return { canCreate: true, remaining: 999, maxPrompts: 999 };
      }
      
      // Get user tier and limit
      const tier = userData.tier || 'basic';
      const maxPrompts = this.config.tierLimits[tier].promptCountLimit;
      
      // Count existing prompts
      const promptsRef = collection(this.db, 'users', userId, 'prompts');
      const promptsSnap = await getDocs(promptsRef);
      const currentCount = promptsSnap.size;
      
      const remaining = Math.max(0, maxPrompts - currentCount);
      const canCreate = currentCount < maxPrompts;
      
      return { 
        canCreate, 
        remaining,
        maxPrompts,
        tier
      };
    } catch (error) {
      logError('[UsageLimiter] Error checking prompt create limit:', error);
      return { canCreate: false, remaining: 0, maxPrompts: 0, error: true };
    }
  }

  // Check if user can use prompts today
  // CRITICAL UPDATE (2025-09-29): Fixed daily reset logic to use local timezone
  // - Previously used UTC date keys causing confusion about reset times
  // - Now resets at midnight local time for consistency
  // - Returns resetTime for UI display of countdown timer
  async checkPromptUsageLimit(userId) {
    if (!userId) {
      return { canUse: false, remaining: 0, maxUsage: 0 };
    }

    try {
      const userRef = doc(this.db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return { canUse: false, remaining: 0, maxUsage: 0 };
      }

      const userData = userDoc.data();

      // Admin bypass
      if (this.config.adminEmails.includes(userData.email)) {
        return { canUse: true, remaining: 999, maxUsage: 999 };
      }

      // Get user tier and limit
      const tier = userData.tier || 'basic';
      const maxUsage = this.config.tierLimits[tier].promptUsageLimit;

      // Get today's date for the usage key (using local timezone)
      // CRITICAL FIX: Using local date string instead of UTC to ensure
      // consistent midnight resets in user's timezone
      const now = new Date();
      const localDateString = this.getLocalDateString(now);
      const promptUsageRef = doc(this.db, 'users', userId, 'promptUsage', localDateString);
      const usageSnap = await getDoc(promptUsageRef);

      if (!usageSnap.exists()) {
        return {
          canUse: true,
          remaining: maxUsage,
          maxUsage,
          resetTime: this.getNextMidnight(), // Return next midnight for UI timer
          tier,
          currentUsage: 0 // Track current usage for display
        };
      }

      const todayUsage = usageSnap.data().count || 0;
      const remaining = Math.max(0, maxUsage - todayUsage);
      const canUse = todayUsage < maxUsage;

      return {
        canUse,
        remaining,
        maxUsage,
        resetTime: this.getNextMidnight(), // Always return next midnight for countdown
        tier,
        currentUsage: todayUsage // Return current usage for "X/7 uses today" display
      };
    } catch (error) {
      logError('[UsageLimiter] Error checking prompt usage limit:', error);
      return { canUse: false, remaining: 0, maxUsage: 0, error: true };
    }
  }

  // Increment prompt usage counter
  async incrementPromptUsage(userId, promptId) {
    if (!userId || !promptId) {
      return { success: false, error: 'Missing user ID or prompt ID' };
    }
    
    try {
      // First check if user can use prompts
      const status = await this.checkPromptUsageLimit(userId);
      if (!status.canUse) {
        return { 
          success: false, 
          error: 'Usage limit reached',
          remaining: status.remaining,
          maxUsage: status.maxUsage
        };
      }
      
      // Get today's date for the usage key (using local timezone)
      // CRITICAL FIX (2025-09-29): Using local timezone for consistent behavior
      const localDateString = this.getLocalDateString();
      const promptUsageRef = doc(this.db, 'users', userId, 'promptUsage', localDateString);
      const usageSnap = await getDoc(promptUsageRef);
      
      // Update usage count
      if (usageSnap.exists()) {
        await updateDoc(promptUsageRef, {
          count: (usageSnap.data().count || 0) + 1,
          updatedAt: serverTimestamp()
        });
      } else {
        await setDoc(promptUsageRef, {
          count: 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      // Also increment usage count on the prompt itself
      const promptRef = doc(this.db, 'users', userId, 'prompts', promptId);
      await runTransaction(this.db, async (transaction) => {
        const promptDoc = await transaction.get(promptRef);
        if (promptDoc.exists()) {
          const currentCount = promptDoc.data().usageCount || 0;
          transaction.update(promptRef, {
            usageCount: currentCount + 1,
            lastUsed: serverTimestamp()
          });
        }
      });
      
      // Get updated status after incrementing
      const updatedStatus = await this.checkPromptUsageLimit(userId);
      
      return { 
        success: true, 
        remaining: updatedStatus.remaining,
        maxUsage: updatedStatus.maxUsage
      };
    } catch (error) {
      logError('[UsageLimiter] Error incrementing prompt usage:', error);
      return { success: false, error: error.message };
    }
  }
}

// Star Coin Checker
// remove old star coin system


/**
 * A custom hook for working with usage limits
 * Optimized version with reduced logging and safer function calls
 * 
 * @param {string} userId - The user ID to check limits for
 * @returns {object} - An object with usage limit methods and state
 */
export function useUsageLimiter(userId) {
  // State for message limits
  const [usageState, setUsageState] = useState({
    canSendMessage: true,
    messageCount: 0,
    remainingMessages: DAILY_MESSAGE_LIMIT, 
    tier: 'basic',
    isInitializing: true
  });

  // State for PickStar limits
  const [pickstarStatus, setPickstarStatus] = useState({
    canUse: false,
    remaining: 0,
    resetTime: null,
    tier: 'basic'
  });
  
  // State for prompt limits
  const [promptsStatus, setPromptsStatus] = useState({
    canCreatePrompt: false,
    remainingPrompts: 0,
    maxPrompts: 0,
    canUsePrompt: false,
    remainingUsage: 0,
    maxUsage: 0,
    loading: true
  });
  
  // Create a memoized limiter instance to avoid recreating it on every render
  const limiter = useMemo(() => new UsageLimiter(), []);

  // Check message usage limit
  const checkLimit = useCallback(async () => {
    if (!userId) return;
    
    try {
      const status = await limiter.checkUserLimit(userId);
      setUsageState(status);
    } catch (error) {
      logError('[useUsageLimiter] Error checking message limit:', error);
      // Provide a fallback state rather than leaving it null
      setUsageState({ 
        canSendMessage: true,
        limit: 'unlimited',
        usage: 0,
        error: true
      });
    }
  }, [limiter, userId]);

  // Increment message usage
  const incrementUsage = useCallback(async () => {
    if (!userId) return;
    
    try {
      await limiter.incrementUsage(userId);
      await checkLimit();
    } catch (error) {
      logError('[useUsageLimiter] Error incrementing usage:', error);
    }
  }, [limiter, userId, checkLimit]);

  // Check PickStar slot limit
  const checkSlotMachineLimit = useCallback(async () => {
    if (!userId) return { canUse: false, remaining: 0, resetTime: null };
    
    try {
      const status = await limiter.checkSlotMachineLimit(userId);
      setPickstarStatus(status);
      return status;
    } catch (error) {
      logError('[useUsageLimiter] Error checking PickStar limit:', error);
      const errorStatus = { canUse: false, remaining: 0, resetTime: null, tier: 'basic', error: true };
      setPickstarStatus(errorStatus);
      return errorStatus;
    }
  }, [limiter, userId]);

  // Increment PickStar slot usage
  const incrementSlotMachineUsage = useCallback(async () => {
    if (!userId) return;
    
    try {
      const result = await limiter.incrementSlotMachineUsage(userId);
      
      // Re-check limits after incrementing
      await checkSlotMachineLimit();
      
      return result;
    } catch (error) {
      logError('[useUsageLimiter] Error incrementing PickStar usage:', error);
    }
  }, [limiter, userId, checkSlotMachineLimit]);

  // Format the reset time for display
  const formatResetTime = useCallback(() => {
    if (!pickstarStatus.resetTime) return "later";
    
    try {
      return formatDistanceToNow(pickstarStatus.resetTime, { addSuffix: true });
    } catch (e) {
      logError('[useUsageLimiter] Error formatting reset time:', e);
      return "soon";
    }
  }, [pickstarStatus.resetTime]);
  
  // Add prompt create limit check
  const checkPromptCreateLimit = useCallback(async () => {
    if (!userId) return;
    
    try {
      const status = await limiter.checkPromptCreateLimit(userId);
      setPromptsStatus(prev => ({
        ...prev,
        canCreatePrompt: status.canCreate,
        remainingPrompts: status.remaining,
        maxPrompts: status.maxPrompts,
        loading: false
      }));
      return status;
    } catch (error) {
      logError('[useUsageLimiter] Error checking prompt create limit:', error);
      return { canCreate: false, remaining: 0, maxPrompts: 0, error: true };
    }
  }, [limiter, userId]);
  
  // Add prompt usage limit check
  const checkPromptUsageLimit = useCallback(async () => {
    if (!userId) return;

    try {
      const status = await limiter.checkPromptUsageLimit(userId);
      setPromptsStatus(prev => ({
        ...prev,
        canUsePrompt: status.canUse,
        remainingUsage: status.remaining,
        maxUsage: status.maxUsage,
        resetTime: status.resetTime,        // Added for countdown timer display
        currentUsage: status.currentUsage,   // Added for "X/7 uses today" display
        loading: false
      }));
      return status;
    } catch (error) {
      logError('[useUsageLimiter] Error checking prompt usage limit:', error);
      return { canUse: false, remaining: 0, maxUsage: 0, error: true };
    }
  }, [limiter, userId]);
  
  // Add prompt usage increment
  const incrementPromptUsage = useCallback(async (promptId) => {
    if (!userId || !promptId) return { success: false };
    
    try {
      const result = await limiter.incrementPromptUsage(userId, promptId);
      
      // Re-check limits after incrementing
      if (result.success) {
        await checkPromptUsageLimit();
      }
      
      return result;
    } catch (error) {
      logError('[useUsageLimiter] Error incrementing prompt usage:', error);
      return { success: false, error: error.message };
    }
  }, [limiter, userId, checkPromptUsageLimit]);
  
  // pickstar slot...Check all limits on component mount
  useEffect(() => {
    if (userId) {
      // Check message limits
      checkLimit().catch(error => {
        logError('[useUsageLimiter] Error in initial checkLimit:', error);
      });
      
      // Check PickStar limits
      checkSlotMachineLimit().catch(error => {
        logError('[useUsageLimiter] Error in initial checkSlotMachineLimit:', error);
      });
      
      // Check prompt limits
      checkPromptCreateLimit().catch(error => {
        logError('[useUsageLimiter] Error in initial checkPromptCreateLimit:', error);
      });
      
      checkPromptUsageLimit().catch(error => {
        logError('[useUsageLimiter] Error in initial checkPromptUsageLimit:', error);
      });
    }
  }, [userId, checkLimit, checkSlotMachineLimit, checkPromptCreateLimit, checkPromptUsageLimit]);

// STAR COIN !!!!!!!!!!!!!!!!!!!!!!!!!!!!
// STAR COIN !!!!!!!!!!!!!!!!!!!!!!!!!!!!
// STAR COIN !!!!!!!!!!!!!!!!!!!!!!!!!!!!

    // This replaces all the Star Coins related state and functions
    const [starCoinsBalance, setStarCoinsBalance] = useState(0);
    const [starCoinsLoading, setStarCoinsLoading] = useState(true);
    
    // Load balance on mount
    useEffect(() => {
      if (userId) {
        setStarCoinsLoading(true);
        starCoinsClient.loadBalance()
          .then(balance => {
            setStarCoinsBalance(balance);
            setStarCoinsLoading(false);
          })
          .catch(error => {
            console.error('Error loading Star Coins balance:', error);
            setStarCoinsLoading(false);
          });
      }
    }, [userId]);

  return {
    // Message limit methods and state
    ...usageState,
    incrementUsage,
    checkLimit,
    
    // PickStar (slot machine) methods and state
    canUse: pickstarStatus.canUse,
    remaining: pickstarStatus.remaining,
    resetTime: pickstarStatus.resetTime,
    tier: pickstarStatus.tier || 'basic',
    formatResetTime,
    checkSlotMachineLimit,
    incrementSlotMachineUsage,
    
    // Custom prompts methods and state
    canCreatePrompt: promptsStatus.canCreatePrompt,
    remainingPrompts: promptsStatus.remainingPrompts,
    maxPrompts: promptsStatus.maxPrompts,
    canUsePrompt: promptsStatus.canUsePrompt,
    remainingUsage: promptsStatus.remainingUsage,
    maxUsage: promptsStatus.maxUsage,
    promptResetTime: promptsStatus.resetTime,       // CRITICAL: Added for countdown timer
    currentPromptUsage: promptsStatus.currentUsage,  // CRITICAL: Added for usage display
    checkPromptCreateLimit,
    checkPromptUsageLimit,
    incrementPromptUsage,

    starCoins: {
      balance: starCoinsBalance,
      loading: starCoinsLoading,
      
      // These are the methods needed by CustomPrompts component
      canUseStarCoinsForFeature: async (feature) => {
        return await starCoinsClient.canUseStarCoinsForFeature(userId, feature);
      },
      
      consumeStarCoins: async (amount, feature) => {
        return await starCoinsClient.consumeStarCoins(amount, feature);
      },
      
      loadBalance: async () => {
        const balance = await starCoinsClient.loadBalance();
        setStarCoinsBalance(balance);
        return balance;
      },
      
      // These methods are required by the existing interface
      // but will just delegate to starCoinsClient
      loadTransactions: async (limit) => {
        return await starCoinsClient.getTransactionHistory(limit);
      },
      
      // Include any static data needed
      packages: STAR_COINS_CONFIG.PACKAGES,
      costs: STAR_COINS_CONFIG.COSTS
    }
  };
}